import type { User } from "@supabase/supabase-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import BrandIcon from "./BrandIcon";
import BrandWordmark from "./BrandWordmark";
import { useTranslation } from "./i18n";
import { useDialogFocus } from "./lib/accessibility";
import { AutomaticBackupPanel, backupErrorKey, backupStatusKey } from "./lib/driveSync/AutomaticBackupPanel";
import { ConflictChoices } from "./lib/driveSync/ConflictChoices";
import { ConnectedDevices } from "./lib/driveSync/ConnectedDevices";
import { DevicePairing, type PairingFeedbackState } from "./lib/driveSync/DevicePairing";
import { syncReviewId } from "./lib/driveSync/reviewScope";
import { SyncSqlAuditPanel } from "./lib/driveSync/SyncSqlAuditPanel";
import { syncMessage } from "./lib/driveSync/syncMessage";
import { useAutomaticBackup } from "./lib/driveSync/useAutomaticBackup";
import { useSyncReview } from "./lib/driveSync/useSyncReview";
import { DRIVE_SYNC_INTERNAL } from "./lib/features";
import type { LocalAccount } from "./lib/localStore/accountScope";
import { hasPendingLocalWrites } from "./lib/localStore/client";
import { supabase } from "./lib/supabase";
import TeacherConsole from "./TeacherConsole";
import "./sync-review.css";

export function DriveOAuthComplete() {
  const { t } = useTranslation("syncReview");
  return (
    <main className="sync-review">
      <h1>{t("inviteTitle")}</h1>
      <p>{t("driveAuthorizationComplete")}</p>
      <button type="button" onClick={() => window.close()}>
        {t("closeAuthorizationPage")}
      </button>
    </main>
  );
}

export default function DriveSyncWorkspace({
  account,
  user,
  onSignOut,
  assertAccess,
}: {
  account: LocalAccount;
  user: User | null;
  onSignOut: () => Promise<void>;
  assertAccess?: () => void;
}) {
  const { t } = useTranslation("syncReview");
  const controller = useSyncReview(account, supabase!);
  const backup = useAutomaticBackup(account, supabase!, controller, assertAccess);
  const { t: backupText } = useTranslation("automaticBackup");
  const [backupOpen, setBackupOpen] = useState(false);
  const closeBackup = () => {
    if (!backup.status.busy) setBackupOpen(false);
  };
  const backupDialog = useDialogFocus<HTMLElement>(backupOpen, closeBackup, () =>
    document.querySelector<HTMLButtonElement>("[data-backup-trigger]"),
  );
  const { status } = controller;
  const [settings, setSettings] = useState(!!syncReviewId());
  const [offering, setOffering] = useState(false);
  const [pairingOpen, setPairingOpen] = useState(false);
  const [pairingMessage, setPairingMessage] = useState<{ text: string; state?: PairingFeedbackState } | null>(null);
  const [pairingError, setPairingError] = useState("");
  const pairingDismissed = useRef(false);
  const showPairingMessage = useCallback((message: string, state?: PairingFeedbackState) => {
    setPairingMessage(message ? { text: message, state } : null);
    if (message && !pairingDismissed.current) setSettings(true);
  }, []);
  useEffect(() => {
    if (
      pairingMessage?.state === "interrupted" &&
      !status.busy &&
      !status.error &&
      status.local &&
      Date.now() < status.local.onlineUntil
    )
      setPairingMessage({ text: t("pairConnected"), state: "success" });
  }, [pairingMessage?.state, status.busy, status.error, status.local, t]);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [remember, setRemember] = useState(false);
  const [googleUrl, setGoogleUrl] = useState("");
  const [binding, setBinding] = useState("");
  const collapsed = useRef(false);
  const privateReview = !!syncReviewId();
  useEffect(() => {
    if (
      privateReview &&
      !collapsed.current &&
      status.local &&
      status.unlocked &&
      status.summary?.drive &&
      !status.error &&
      !status.busy
    ) {
      collapsed.current = true;
      setSettings(false);
    }
  }, [privateReview, status.local, status.unlocked, status.summary?.drive, status.error, status.busy]);
  const perform = async (action: () => Promise<void>) => {
    if (working) return;
    setWorking(true);
    setError("");
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : "sync-control-unavailable");
    } finally {
      setWorking(false);
    }
  };
  const checkAuthorization = () =>
    perform(async () => {
      await controller.inspectAccount();
      // Display the selected account before binding it. A completed Google tab
      // does not authorize switching this workspace to another Drive.
      await controller.run();
    }).then(() => undefined);
  const pairingConnecting =
    pairingMessage?.state === "connecting" || (pairingMessage?.state === "interrupted" && status.busy);
  const syncState = !status.ready
    ? "checking"
    : !status.local
      ? status.summary?.root && !status.setupPending
        ? "pairRequired"
        : "notEnabled"
      : !status.unlocked
        ? "reconnect"
        : status.conflicts.length
          ? "conflict"
          : status.error || !navigator.onLine
            ? "reconnect"
            : status.concurrentEditing
              ? "concurrentEditing"
              : pairingConnecting
                ? "connecting"
                : status.pending
                  ? "pending"
                  : Date.now() < status.local.onlineUntil
                    ? "synced"
                    : "reconnect";
  const problem = error || status.error;
  const candidate = status.summary?.oauthCandidate;
  const wrongDrive = !!candidate && !!status.summary?.drive && candidate.subject !== status.summary.drive.subject;
  // A fresh paired store is empty until its first Drive download completes.
  // Do not let the classroom wizard open against that temporary empty view.
  // highWater survives a later disconnect, so existing offline data stay visible.
  const classroomReady =
    status.ready &&
    (privateReview
      ? status.local && (status.summary?.drive || !navigator.onLine || status.error)
      : status.local
        ? status.local.highWater > 0 || (status.summary && !status.summary.drive)
        : status.summary && !status.summary.root);
  const closeSettings = () => {
    if (working) return;
    pairingDismissed.current = true;
    setSettings(false);
    setOffering(false);
  };
  const dialogOpen = !!(!privateReview && classroomReady && (settings || offering));
  const dialogRef = useDialogFocus<HTMLElement>(dialogOpen, closeSettings, () =>
    document.querySelector<HTMLButtonElement>("[data-sync-settings-trigger]"),
  );
  useEffect(() => {
    if (!privateReview && status.conflicts.length > 0) setSettings(true);
  }, [privateReview, status.conflicts.length]);
  if (!status.ready)
    return (
      <main className="loading-screen" aria-busy="true">
        <BrandIcon className="brand-loader" />
        <p role="status">{t("loadingClassroom")}</p>
      </main>
    );
  const phase = candidate || status.summary?.drive ? 3 : status.local ? 2 : 1;
  const connected = !!status.local && !!status.summary?.drive;
  const controls = (
    <section className="sync-setup-stage">
      <h2>{t("authorizeDriveTitle")}</h2>
      {!status.summary?.drive && <p>{t("connectDriveNext")}</p>}
      <div className="sync-action-row">
        <button
          type="button"
          className={googleUrl ? "" : "primary-action"}
          disabled={working || status.busy}
          onClick={() =>
            void perform(async () => {
              const result = await controller.api.current!.request<{ url: string }>("oauth-start", {
                returnOrigin: location.origin,
                returnPath: privateReview ? "/sync-review" : "/app",
              });
              setGoogleUrl(result.url);
            })
          }
        >
          {t("googleStart")}
        </button>
        {googleUrl && (
          <a className="sync-primary-link" href={googleUrl} target="_blank" rel="noopener noreferrer">
            {t("googleOpen")} <span aria-hidden="true">↗</span>
          </a>
        )}
        {(privateReview || googleUrl) && (
          <button type="button" disabled={working || status.busy} onClick={() => void checkAuthorization()}>
            {t("googleCheck")}
          </button>
        )}
      </div>
      {googleUrl && <p className="sync-quiet">{t("returnAfterGoogle")}</p>}
    </section>
  );
  const inlineConcurrentNotice = dialogOpen || privateReview || !classroomReady;
  const concurrentNotice = status.concurrentEditing && (
    <div
      className={`sync-concurrent-notice${inlineConcurrentNotice ? " sync-concurrent-notice-inline" : ""}`}
      role="alert"
      aria-atomic="true"
    >
      <div>
        <strong>{t("concurrentEditing")}</strong>
        <p>{t("concurrentEditingHint")}</p>
      </div>
      <button type="button" onClick={controller.dismissConcurrentEditing}>
        {t("concurrentEditingDismiss")}
      </button>
    </div>
  );
  const statusLine = (
    <div className="sync-review-status" role="status">
      <span>{t(hasPendingLocalWrites() ? "saving" : "saved")}</span>
      <span data-sync-state={syncState}>{t(syncState)}</span>
    </div>
  );
  const panel = (
    <section
      className={`sync-review${privateReview ? "" : " sync-settings-shell"}`}
      aria-labelledby="sync-review-title"
    >
      <div className="sync-review-heading">
        <div className="sync-panel-title">
          {!privateReview && <BrandIcon className="sync-panel-mark" />}
          <div>
            {!privateReview && <small className="sync-eyebrow">{t("crossDeviceLabel")}</small>}
            <h1 id="sync-review-title">{t(privateReview ? "title" : "inviteTitle")}</h1>
            {privateReview && <p>{t("intro")}</p>}
          </div>
        </div>
        {privateReview ? (
          <button type="button" onClick={() => setSettings((value) => !value)} aria-expanded={settings}>
            {t("settings")}
          </button>
        ) : classroomReady ? (
          <button
            type="button"
            className="sync-close"
            aria-label={t("closeSettings")}
            disabled={working}
            onClick={closeSettings}
          >
            <span aria-hidden="true">×</span>
          </button>
        ) : null}
      </div>
      {statusLine}
      {inlineConcurrentNotice && concurrentNotice}
      {pairingMessage && (
        <p className="sync-pairing-status" role="status">
          {(pairingMessage.state === "waiting" || pairingConnecting) &&
            !(status.busy && Date.now() - status.progressAt > 30000) && (
              <span className="sync-pairing-spinner" aria-hidden="true" />
            )}
          {pairingMessage.state === "success" && (
            <span className="sync-pairing-check" aria-hidden="true">
              ✓
            </span>
          )}
          <span>
            {pairingMessage.state === "interrupted" && pairingConnecting ? t("pairConnecting") : pairingMessage.text}
            {pairingConnecting && status.busy && status.progress && (
              <small className="sync-pairing-progress">
                {t(
                  (
                    {
                      checking: "pairProgressChecking",
                      checkpoint: "pairProgressCheckpoint",
                      listing: "pairProgressListing",
                      downloading: "pairProgressDownloading",
                      applying: "pairProgressApplying",
                      uploading: "pairProgressUploading",
                      finishing: "pairProgressFinishing",
                    } as const
                  )[status.progress.phase],
                )}
                {status.progress.total !== undefined &&
                  ["checkpoint", "downloading"].includes(status.progress.phase) &&
                  ` ${t("pairProgressCount", { completed: status.progress.completed, total: status.progress.total })}`}
                {Date.now() - status.progressAt > 30000 && <span> · {t("pairSlow")}</span>}
              </small>
            )}
          </span>
        </p>
      )}
      {pairingError && <p role="alert">{pairingError}</p>}
      {problem && <p role="alert">{t(syncMessage(problem))}</p>}
      {(!privateReview || settings) && (
        <div className="sync-review-settings">
          {!privateReview && !connected && (
            <ol className="sync-setup-progress" aria-label={t("setupProgress")}>
              {(["deviceStep", "googleStep", "confirmStep"] as const).map((key, index) => (
                <li
                  key={key}
                  className={phase === index + 1 ? "current" : phase > index + 1 ? "complete" : ""}
                  aria-current={phase === index + 1 ? "step" : undefined}
                >
                  <span className="sync-step-number" aria-hidden="true">
                    {phase > index + 1 ? "✓" : index + 1}
                  </span>
                  {t(key)}
                </li>
              ))}
            </ol>
          )}
          {!status.local && status.summary && (!status.summary.root || status.setupPending) && (
            <section className="sync-setup-stage">
              <h2>{t("firstDeviceTitle")}</h2>
              <p>{t("automaticSetupHint")}</p>
              <label className="sync-review-check">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(e) => setRemember(e.target.checked)}
                  disabled={working}
                />
                {t("privateDevice")}
              </label>
              <button
                type="button"
                className="primary-action"
                disabled={!remember || working || !status.ready}
                onClick={() =>
                  void perform(async () => {
                    await assertAccess?.();
                    setSettings(true);
                    await controller.activateAutomatic();
                    await controller.run();
                  })
                }
              >
                {t(working ? "working" : status.setupPending ? "continueSetup" : "enableDriveSync")}
              </button>
            </section>
          )}
          {!status.local && status.summary?.root && !status.setupPending && <p>{t("existingPairedRoot")}</p>}
          {connected && !privateReview && (
            <div className="sync-connected-card">
              <span className="sync-connected-check" aria-hidden="true">
                ✓
              </span>
              <div>
                <h2>{t("connectedTitle")}</h2>
                <p>{t("boundDrive", { email: status.summary?.drive?.displayEmail ?? "" })}</p>
                <p className="sync-storage-hint">{t("driveStorageHint")}</p>
              </div>
            </div>
          )}
          {status.local && status.unlocked && status.summary?.root && <ConnectedDevices controller={controller} />}
          {connected && !privateReview && (
            <button
              type="button"
              className="sync-device-action"
              aria-expanded={pairingOpen}
              onClick={() => setPairingOpen((value) => !value)}
            >
              <SyncGlyph /> {t("deviceManagement")}{" "}
              <span className="sync-device-toggle-mark" aria-hidden="true">
                {pairingOpen ? "−" : "+"}
              </span>
            </button>
          )}
          {status.summary?.root &&
            controller.client &&
            (privateReview || !status.local || (connected && pairingOpen)) && (
              <DevicePairing
                controller={controller}
                workspaceId={account.workspaceId}
                onMessage={showPairingMessage}
                onError={setPairingError}
              />
            )}
          {status.local &&
            status.unlocked &&
            !candidate &&
            (connected ? (
              <details className="sync-secondary-settings">
                <summary>{t("manageDrive")}</summary>
                {controls}
              </details>
            ) : (
              controls
            ))}
          {candidate && (
            <section className="sync-setup-stage">
              <h2>{t("binding")}</h2>
              <dl className="sync-account-list">
                <div>
                  <dt>{t("appAccount")}</dt>
                  <dd>{account.email}</dd>
                </div>
                <div>
                  <dt>{t("driveAccount")}</dt>
                  <dd>
                    <span>{candidate.displayEmail}</span>
                    <p className="sync-storage-hint">{t("driveStorageHint")}</p>
                  </dd>
                </div>
              </dl>
              {wrongDrive ? (
                <p role="alert">{t("differentDriveRetained")}</p>
              ) : (
                <>
                  <label className="sync-review-check">
                    <input
                      type="checkbox"
                      checked={binding === candidate.subject}
                      onChange={(e) => setBinding(e.target.checked ? candidate.subject : "")}
                      disabled={working}
                    />
                    {t("bindingConfirm")}
                  </label>
                  <button
                    type="button"
                    className="primary-action"
                    disabled={working || binding !== candidate.subject}
                    onClick={() =>
                      void perform(async () => {
                        await controller.api.current!.request("oauth-complete", { confirmedSubject: binding });
                        controller.api.current!.clearTransport();
                        setGoogleUrl("");
                        setBinding("");
                        await controller.run();
                      })
                    }
                  >
                    {t("bind")}
                  </button>
                </>
              )}
            </section>
          )}
          {controller.client && status.conflicts.length > 0 && (
            <ConflictChoices
              client={controller.client}
              workspaceId={account.workspaceId}
              conflicts={status.conflicts}
              onResolved={async () => {
                await controller.refresh();
                await controller.run();
              }}
            />
          )}
          <details className="sync-secondary-settings">
            <summary>{t("syncUsage")}</summary>
            <p>{t("syncUsageSteps")}</p>
            <p>{t("noBackground")}</p>
            <p>{t("rememberedDeviceLoss")}</p>
          </details>
          {status.local && (
            <div className="sync-panel-footer">
              <button
                type="button"
                disabled={working || status.busy}
                onClick={() =>
                  void (async () => {
                    const resumingPair = pairingMessage?.state === "interrupted";
                    if (resumingPair) showPairingMessage(t("pairConnecting"), "connecting");
                    const failure = await controller.run();
                    if (resumingPair)
                      showPairingMessage(
                        t(failure ? "pairInterrupted" : "pairConnected"),
                        failure ? "interrupted" : "success",
                      );
                  })()
                }
              >
                {t("retry")}
              </button>
              {!privateReview && classroomReady && (
                <button type="button" className="primary-action" disabled={working} onClick={closeSettings}>
                  {t("backToClassroom")}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
  const trigger = (
    <button
      type="button"
      className="sync-status-trigger"
      data-sync-settings-trigger
      data-state={syncState}
      title={t("settings")}
      aria-label={t("settings")}
      aria-expanded={settings || offering}
      onClick={() => {
        pairingDismissed.current = false;
        setOffering(false);
        setSettings(true);
      }}
    >
      <SyncGlyph /> <span role="status">{t(settings || offering ? "settings" : syncState)}</span>
    </button>
  );
  const backupTrigger = (
    <span className="topbar-backup-tools">
      {backup.status.ready && backup.status.config?.enabled && backup.status.unlocked && (
        <button
          type="button"
          className="topbar-backup"
          title={backupText("leaveHint")}
          aria-label={backupText("now")}
          disabled={backup.status.busy}
          onClick={() => {
            setSettings(false);
            setOffering(false);
            setBackupOpen(true);
            void backup.backupNow();
          }}
        >
          <span>{backupText("now")}</span>
          <small aria-hidden="true">{backupText("leaveShort")}</small>
        </button>
      )}
      <button
        type="button"
        className="topbar-backup"
        data-backup-trigger
        aria-label={backupText("title")}
        aria-haspopup="dialog"
        aria-expanded={backupOpen}
        onClick={() => {
          setSettings(false);
          setOffering(false);
          setBackupOpen(true);
          void backup.refresh();
        }}
      >
        <span>{backupText("title")}</span>
        {backup.status.config && <small aria-hidden="true">{backupText(backupStatusKey(backup.status))}</small>}
      </button>
    </span>
  );
  return (
    <>
      {!inlineConcurrentNotice && concurrentNotice}
      {backup.status.notice && (
        <div className="sync-review-status" role="alert">
          <span>{backupText(backupErrorKey(backup.status.error))}</span>
          <button type="button" onClick={() => setBackupOpen(true)}>
            {backupText("title")}
          </button>
          <button type="button" onClick={backup.dismissNotice}>
            {backupText("dismiss")}
          </button>
        </div>
      )}
      {privateReview
        ? panel
        : !classroomReady && (
            <main className="sync-connect-page">
              <header>
                <BrandWordmark />
                <span className="sync-eyebrow">{t("crossDeviceLabel")}</span>
              </header>
              {panel}
              {backupTrigger}
            </main>
          )}
      {DRIVE_SYNC_INTERNAL && new URLSearchParams(location.search).has("sql-audit") && (
        <div className="sync-audit-wrap">
          <SyncSqlAuditPanel controller={controller} workspaceId={account.workspaceId} />
        </div>
      )}
      {classroomReady && (
        <TeacherConsole
          key={`${account.workspaceId}/${status.local?.datasetId ?? "local"}/${status.local?.generation ?? "local"}`}
          user={user}
          preview={false}
          localMode
          localAccount={account}
          syncControl={privateReview ? undefined : trigger}
          backupControl={backupTrigger}
          guidancePaused={dialogOpen || backupOpen}
          onInitialClassSetupComplete={() => {
            if (!status.local && !status.summary?.root) setOffering(true);
          }}
          assertWritable={() => {
            assertAccess?.();
            if (status.local || privateReview) controller.assertClassroom();
          }}
          onExitPreview={() => {
            location.href = "/";
          }}
          onSignOut={onSignOut}
        />
      )}
      {backupOpen &&
        createPortal(
          <div className="sync-dialog-backdrop">
            <section
              ref={backupDialog}
              role="dialog"
              aria-modal="true"
              aria-labelledby="automatic-backup-title"
              tabIndex={-1}
              className="sync-settings-dialog automatic-backup-dialog"
            >
              <header className="sync-review-heading">
                <h1 id="automatic-backup-title">{backupText("title")}</h1>
                <button type="button" disabled={backup.status.busy} onClick={closeBackup}>
                  {backupText("close")}
                </button>
              </header>
              <AutomaticBackupPanel controller={backup} newDevice={!status.local} />
            </section>
          </div>,
          document.body,
        )}
      {dialogOpen &&
        createPortal(
          <div className="sync-dialog-backdrop">
            <section
              ref={dialogRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={offering ? "sync-offer-title" : "sync-review-title"}
              tabIndex={-1}
              className="sync-settings-dialog"
            >
              {offering ? (
                <div className="sync-offer">
                  <div className="sync-offer-illustration" aria-hidden="true">
                    <SyncGlyph />
                  </div>
                  <small className="sync-eyebrow">{t("optionalStep")}</small>
                  <h1 id="sync-offer-title">{t("offerTitle")}</h1>
                  <p>{t("offerDescription")}</p>
                  <div className="sync-action-row">
                    <button
                      type="button"
                      className="primary-action"
                      onClick={() => {
                        setOffering(false);
                        setSettings(true);
                      }}
                    >
                      {t("offerEnable")}
                    </button>
                    <button type="button" onClick={closeSettings}>
                      {t("offerLater")}
                    </button>
                  </div>
                  <small className="sync-quiet">{t("offerLaterHint")}</small>
                </div>
              ) : (
                panel
              )}
            </section>
          </div>,
          document.body,
        )}
    </>
  );
}

function SyncGlyph() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 8a8.5 8.5 0 0 0-14.4-2L3 9m0-5v5h5M4 16a8.5 8.5 0 0 0 14.4 2L21 15m0 5v-5h-5" />
    </svg>
  );
}
